import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Upload, X, Loader2, CheckCircle, AlertCircle } from "lucide-react";

export default function BulkImportTeachers({ onClose, onSuccess }) {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [progress, setProgress] = useState("");
  const [importData, setImportData] = useState("");

  const processImportData = async () => {
    if (!importData.trim()) {
      setError("Please paste teacher data");
      return;
    }

    setProcessing(true);
    setError(null);
    setProgress("Parsing data...");

    try {
      const lines = importData.trim().split('\n').map(line => line.trim()).filter(line => line);
      
      if (lines.length === 0) {
        throw new Error("No data found");
      }

      // Parse header line
      const headerLine = lines[0];
      const headers = headerLine.split('\t').map(h => h.trim().toLowerCase());
      
      // Map column indices
      const colMap = {
        teacherName: headers.findIndex(h => 
          h.includes('teacher name') || 
          h.includes('full name') || 
          h === 'name' || 
          h === 'teacher'
        ),
        email: headers.findIndex(h => 
          h.includes('email') || 
          h.includes('e-mail')
        ),
        gradeLevel: headers.findIndex(h => 
          h.includes('grade') && h.includes('level') ||
          h === 'grade'
        ),
      };

      // Check required columns
      if (colMap.teacherName === -1 || colMap.email === -1) {
        throw new Error("Missing required columns (Teacher Name and Email)");
      }

      setProgress("Loading existing users...");
      const allUsers = await base44.entities.User.list();
      
      let updatedCount = 0;
      let notFoundCount = 0;
      let skippedCount = 0;

      // Process data lines (skip header)
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i];
        const cols = line.split('\t').map(c => c.trim());

        setProgress(`Processing ${i} of ${lines.length - 1}...`);

        const teacherName = cols[colMap.teacherName];
        const email = cols[colMap.email];
        const gradeLevel = colMap.gradeLevel !== -1 ? cols[colMap.gradeLevel] : '';

        // Skip if missing required fields
        if (!teacherName || !email) {
          skippedCount++;
          continue;
        }

        // Find user by email (case-insensitive)
        const existingUser = allUsers.find(
          u => u.email?.toLowerCase() === email.toLowerCase()
        );

        if (existingUser) {
          // Update user with teacher name and optionally grade level
          const updateData = {
            teacher_full_name: teacherName
          };
          
          // Only update grade level if provided
          if (gradeLevel) {
            updateData.teacher_grade_level = gradeLevel;
          }
          
          await base44.entities.User.update(existingUser.id, updateData);
          updatedCount++;
        } else {
          // User not found in system
          notFoundCount++;
        }
      }

      let resultMessage = `✅ Success! Updated ${updatedCount} teacher${updatedCount !== 1 ? 's' : ''} with full names`;
      if (notFoundCount > 0) {
        resultMessage += `. ⚠️ ${notFoundCount} email${notFoundCount !== 1 ? 's' : ''} not found in system (users must be invited first)`;
      }
      if (skippedCount > 0) {
        resultMessage += `. Skipped ${skippedCount} incomplete records`;
      }
      resultMessage += '.';

      setSuccess(resultMessage);
      
      setTimeout(() => {
        onSuccess();
      }, 3000);

    } catch (err) {
      console.error("Processing error:", err);
      setError(err.message || "Failed to process data. Make sure you copied the complete data with all columns.");
    } finally {
      setProcessing(false);
      setProgress("");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <Card className="w-full max-w-4xl my-8">
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5" />
              Bulk Update Teacher Names
            </CardTitle>
            <Button variant="ghost" size="icon" onClick={onClose} disabled={processing}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="bg-green-50 text-green-900 border-green-200">
              <CheckCircle className="w-4 h-4" />
              <AlertDescription>{success}</AlertDescription>
            </Alert>
          )}

          {progress && processing && (
            <Alert className="bg-blue-50 text-blue-900 border-blue-200">
              <Loader2 className="w-4 h-4 animate-spin" />
              <AlertDescription>{progress}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <div className="space-y-2">
              <Alert className="mb-4 bg-blue-50 border-blue-200">
                <AlertDescription className="text-sm space-y-2">
                  <div>
                    <strong>📋 Instructions:</strong>
                  </div>
                  <ol className="list-decimal list-inside space-y-1 ml-2">
                    <li>Open your teacher list in Excel/CSV</li>
                    <li><strong>Required columns:</strong> Teacher Name (as it appears on reports), Email</li>
                    <li><strong>Optional column:</strong> Grade Level (you can add this later via Users page)</li>
                    <li>Select ALL columns from header row down</li>
                    <li>Copy (Ctrl+C or Cmd+C)</li>
                    <li>Paste below (Ctrl+V or Cmd+V)</li>
                    <li>Click "Update Teacher Names"</li>
                  </ol>
                  <div className="text-xs text-blue-700 mt-2 space-y-1">
                    <p>ℹ️ <strong>Matching:</strong> Teachers are matched by email address.</p>
                    <p>⚠️ <strong>Important:</strong> Teachers must already exist in the system (invited via Dashboard → Users). This only updates their display names.</p>
                    <p>💡 <strong>Example:</strong> If a teacher logs in as "cnewsome@iltexas.org", this will display them as "CHANTELLE NEWSOME" throughout the system.</p>
                  </div>
                </AlertDescription>
              </Alert>
              <Textarea
                value={importData}
                onChange={(e) => {
                  setImportData(e.target.value);
                  setError(null);
                }}
                placeholder="Paste teacher data here (must include headers: Teacher Name, Email, and optionally Grade Level)..."
                className="h-80 font-mono text-xs"
                disabled={processing}
              />
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-4 border-t">
            <Button variant="outline" onClick={onClose} disabled={processing}>
              Cancel
            </Button>
            <Button 
              onClick={processImportData}
              disabled={!importData.trim() || processing}
              className="bg-green-600 hover:bg-green-700"
            >
              {processing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Update Teacher Names
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}